import { TurnoverType } from "../../../Types/turnoverTypes"
import { Form, Formik } from "formik"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"
import { DateInputField } from "../../FormFields/DateInput/DateInputField"
import { Button, Col, Row } from "react-bootstrap"
import { useState } from "react"
import SelectUserModal from "../../Modals/SelectUserModal/selectUserModal"
import { SelectPropertyModal } from "../../Modals/SelectPropertyModal/selectPropertyModal"
import useUserRefs from "../../../Hooks/UseUsersQuery/UseUserRefs"
import { useSelector } from "react-redux"
import { accountSelector } from "../../../redux/account/accountSlice"

const generateBlankTurnover = () => ({
    propertyId: '',
    bookingId: '',
    type: '',
    status: 'pending',
    scheduledFor: new Date(),
    assignedTo: '',
    completedAt: '',
    completedBy: '',
    notes: '',
    createdBy: '',
})

export default function TurnoverForm(props: {
    disabled: boolean,
    onSubmit: (v: any) => Promise<any>,
    initialValues?: Partial<TurnoverType>,
}) {
    const {
        disabled,
        onSubmit,
        initialValues,
    } = props

    const { _id: currentUserId } = useSelector(accountSelector)
    const userRefs = useUserRefs()
    const [selectAssignedUserModal, setSelectAssignedUserModal] = useState(false)
    const [selectCompletedByUserModal, setSelectCompletedByUserModal] = useState(false)
    const [selectPropertyModal, setSelectPropertyModal] = useState(false)

    return (
        <div className="turnover-form">
            <Formik
                initialValues={{
                    ...generateBlankTurnover(),
                    ...(initialValues ? initialValues : {}),
                    createdBy: initialValues?.createdBy || currentUserId || '',
                }}
                validate={values => {
                    const errors: any = {};

                    if (!values.propertyId) {
                        errors.propertyId = 'Property is required';
                    }

                    if (!values.type) {
                        errors.type = 'Type is required';
                    }

                    if (!values.status) {
                        errors.status = 'Status is required';
                    }

                    return errors;
                }}
                onSubmit={async (values, { setSubmitting }) => {
                    if (onSubmit) {
                        try {
                            onSubmit({ ...values })
                        } catch (err) {
                            // custom error handler
                        }
                    }
                    setSubmitting(false)
                }}
            >
                {({ values, isSubmitting, errors, setFieldValue }) => {
                    return (
                        <>
                            <Form>
                                <h3>Property Selection</h3>
                                <TextInputField
                                    fieldType="text"
                                    fieldLabel="Property ID"
                                    placeholder="Property ID"
                                    fieldName="propertyId"
                                    disabled={true}
                                />
                                <div className="mb-3">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setSelectPropertyModal(true)}
                                        disabled={disabled}
                                    >
                                        Select Property
                                    </Button>
                                    {values.propertyId && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => {
                                                setFieldValue('propertyId', '')
                                            }}
                                            disabled={disabled}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>

                                <h3>Turnover Information</h3>
                                <TextInputField
                                    fieldType="text"
                                    fieldLabel="Booking ID (Optional)"
                                    placeholder="Booking ID"
                                    fieldName="bookingId"
                                />

                                <Row>
                                    <Col xs={12} md={6}>
                                        <SelectInputField
                                            fieldLabel="Type"
                                            placeholder="Select Type"
                                            fieldName="type"
                                            options={[
                                                { value: 'checkOut', text: 'Check Out' },
                                                { value: 'fullClean', text: 'Full Clean' },
                                                { value: 'turnDown', text: 'Turn Down' },
                                                { value: 'refresh', text: 'Refresh' },
                                            ]}
                                        />
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <SelectInputField
                                            fieldLabel="Status"
                                            placeholder="Select Status"
                                            fieldName="status"
                                            options={[
                                                { value: 'new', text: 'New' },
                                                { value: 'pending', text: 'Pending' },
                                                { value: 'started', text: 'Started' },
                                                { value: 'completed', text: 'Completed' },
                                                { value: 'ready', text: 'Ready' },
                                            ]}
                                        />
                                    </Col>
                                </Row>

                                <DateInputField
                                    fieldLabel="Scheduled For"
                                    placeholder="Scheduled For"
                                    fieldName="scheduledFor"
                                />

                                <h3>Assignment</h3>
                                <TextInputField
                                    fieldType="text"
                                    fieldLabel="Created By"
                                    placeholder="Created By"
                                    fieldName="createdBy"
                                    disabled={true}
                                />
                                {values.createdBy && userRefs[values.createdBy] && (
                                    <div className="mb-3">
                                        <small className="text-muted">
                                            Created By: <strong>{userRefs[values.createdBy].firstName} {userRefs[values.createdBy].lastName}</strong> ({userRefs[values.createdBy].email})
                                        </small>
                                    </div>
                                )}

                                <TextInputField
                                    fieldType="text"
                                    fieldLabel="Assigned To"
                                    placeholder="Assigned To (Optional)"
                                    fieldName="assignedTo"
                                    disabled={true}
                                />
                                {values.assignedTo && userRefs[values.assignedTo] && (
                                    <div className="mb-3">
                                        <small className="text-muted">
                                            Assigned To: <strong>{userRefs[values.assignedTo].firstName} {userRefs[values.assignedTo].lastName}</strong> ({userRefs[values.assignedTo].email})
                                        </small>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setSelectAssignedUserModal(true)}
                                        disabled={disabled}
                                    >
                                        Assign User
                                    </Button>
                                    {values.assignedTo && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => {
                                                setFieldValue('assignedTo', '')
                                            }}
                                            disabled={disabled}
                                        >
                                            Clear Assignment
                                        </Button>
                                    )}
                                </div>

                                <h3>Completion</h3>
                                <DateInputField
                                    fieldLabel="Completed At"
                                    placeholder="Completed At"
                                    fieldName="completedAt"
                                />

                                <TextInputField
                                    fieldType="text"
                                    fieldLabel="Completed By"
                                    placeholder="Completed By (Optional)"
                                    fieldName="completedBy"
                                    disabled={true}
                                />
                                {values.completedBy && userRefs[values.completedBy] && (
                                    <div className="mb-3">
                                        <small className="text-muted">
                                            Completed By: <strong>{userRefs[values.completedBy].firstName} {userRefs[values.completedBy].lastName}</strong> ({userRefs[values.completedBy].email})
                                        </small>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setSelectCompletedByUserModal(true)}
                                        disabled={disabled}
                                    >
                                        Select Completed By
                                    </Button>
                                    {values.completedBy && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => {
                                                setFieldValue('completedBy', '')
                                            }}
                                            disabled={disabled}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>

                                <h3>Notes</h3>
                                <TextInputField
                                    fieldType="textarea"
                                    fieldLabel="Notes"
                                    placeholder="Notes"
                                    fieldName="notes"
                                />

                                <hr />
                                <div className="mb-3">
                                    Errors: {JSON.stringify(errors)}
                                </div>

                                <Button
                                    className="mb-3"
                                    type="submit"
                                    disabled={Object.keys(errors).length > 0 || isSubmitting || disabled}
                                >
                                    Submit
                                </Button>

                            </Form>

                            <SelectPropertyModal
                                active={selectPropertyModal}
                                onClose={() => {
                                    setSelectPropertyModal(false)
                                }}
                                title="Select Property"
                                message="Please select a property for this turnover:"
                                onSelect={(selectedProperty: any) => {
                                    if (selectedProperty._id) {
                                        setFieldValue('propertyId', selectedProperty._id)
                                    }
                                    setSelectPropertyModal(false)
                                }}
                            />

                            <SelectUserModal
                                active={selectAssignedUserModal}
                                onClose={() => {
                                    setSelectAssignedUserModal(false)
                                }}
                                title="Assign User"
                                message="Please select a user to assign this turnover to:"
                                onSelect={(selectedUser: any) => {
                                    if (selectedUser._id) {
                                        setFieldValue('assignedTo', selectedUser._id)
                                    }
                                    setSelectAssignedUserModal(false)
                                }}
                            />

                            <SelectUserModal
                                active={selectCompletedByUserModal}
                                onClose={() => {
                                    setSelectCompletedByUserModal(false)
                                }}
                                title="Select Completed By"
                                message="Please select the user who completed this turnover:"
                                onSelect={(selectedUser: any) => {
                                    if (selectedUser._id) {
                                        setFieldValue('completedBy', selectedUser._id)
                                    }
                                    setSelectCompletedByUserModal(false)
                                }}
                            />
                        </>
                    )
                }}
            </Formik>
        </div>
    )
}
